import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import DownloadDialog from './DownloadDialog';
import { deliverFiles } from './lib/deliverFiles';
import './SessionReview.css';

function SessionReview() {
  const { code } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [images, setImages] = useState(location.state?.images || []);
  const [loading, setLoading] = useState(!location.state?.images);
  const [copied, setCopied] = useState(false);
  const [title, setTitle] = useState(location.state?.title || '');
  const [isEditingTitle, setIsEditingTitle] = useState(false);

  // Override global overflow:hidden on body/html so this page can scroll on mobile
  useEffect(() => {
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    // No code and no images from state — nothing to show
    if (!code && images.length === 0) {
      navigate('/', { replace: true });
      return;
    }
    // Have code but no images yet — fetch from API
    if (code && images.length === 0) {
      fetch(`/api/sessions/${code}`)
        .then(res => {
          if (!res.ok) throw new Error('Session not found');
          return res.json();
        })
        .then(data => {
          setImages(data.images);
          setTitle(data.title || '');
          setLoading(false);
        })
        .catch(() => {
          navigate('/', { replace: true });
        });
    }
  }, [code, navigate, images.length]);

  const [showDownload, setShowDownload] = useState(false);

  // Archive.org answers 200 with its own logo for items that have no image, so
  // a notfound URL is a known-dead entry. Handing it to the dialog would inflate
  // the total and guarantee a failure. Dashboard filters the same way.
  const downloadableImages = images.filter((image) => {
    const url = image.fullSizeUrl || image.thumbnailUrl;
    return !!url && url !== 'https://archive.org/images/notfound.png';
  });

  const deliver = (files, setPercent) =>
    deliverFiles(files, {
      zipName: `session-${code || 'images'}.zip`,
      onZipPercent: setPercent,
    });

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const saveTitle = async (newTitle) => {
    setTitle(newTitle);
    setIsEditingTitle(false);
    if (code) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`/api/sessions/${code}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token,
          },
          body: JSON.stringify({ title: newTitle }),
        });
      } catch (err) {
        console.error('Error saving title:', err);
      }
    }
  };

  if (loading) {
    return <div className="session-review"><div className="session-review-loading">Loading...</div></div>;
  }

  if (images.length === 0) {
    navigate('/', { replace: true });
    return null;
  }

  return (
    <div className="session-review">
      {isEditingTitle ? (
        <input
          className="session-review-title-input"
          defaultValue={title}
          autoFocus
          onBlur={(e) => saveTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') saveTitle(e.target.value); }}
        />
      ) : (
        <h1
          className="session-review-title"
          onClick={() => setIsEditingTitle(true)}
          title="Click to rename"
        >
          {title || 'untitled'}
        </h1>
      )}
      <div className="session-review-images">
        {images.map((item, index) => (
          <div key={`review-${item.id}-${index}`} className="session-review-image">
            <img
              src={item.thumbnailUrl || item.fullSizeUrl}
              alt={item.name}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://archive.org/images/notfound.png";
              }}
            />
          </div>
        ))}
      </div>
      <div className="session-review-actions">
        {code && (
          <button onClick={copyLink} className="session-review-button">
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        )}
        <button
          onClick={() => setShowDownload(true)}
          className="session-review-button"
          disabled={images.length === 0}
        >
          Download
        </button>
        <button onClick={() => navigate('/')} className="session-review-button">
          New Session
        </button>
        <button onClick={() => navigate('/dashboard')} className="session-review-button">
          My Boards
        </button>
      </div>

      {showDownload && (
        <DownloadDialog
          images={downloadableImages}
          onClose={() => setShowDownload(false)}
          onReady={deliver}
        />
      )}
    </div>
  );
}

export default SessionReview;
